import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { ACTIVIDADES_ECONOMICAS, ACTIVIDADES_GRUPOS } from "@/lib/catalogos_maestros";

// Combobox aditivo (fase 8 fix B-03).
// Reemplaza el <Select> plano de "Actividad económica" por un buscador real que:
//   1) Filtra por texto sin distinguir mayúsculas ni acentos.
//   2) Mantiene el agrupamiento por sección (Comercio, Servicios, ...).
//   3) Conserva la etiqueta [AV] visible.
//   4) Incluye la opción "Otro (especificar)".
// Compatible con el mismo contrato del <Select> anterior: value=string, onValueChange(v).
const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Item extra "Otro (especificar)" que ya usaba el Select original.
const OTRO = { value: "Otro (especificar)", grupo: "Otro" };

export function ActividadEconomicaCombobox({ value, onValueChange, id, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const items = useMemo(() => {
    const q = norm(query);
    const gruposConItems = [];
    for (const g of ACTIVIDADES_GRUPOS) {
      const opts = ACTIVIDADES_ECONOMICAS.filter((a) => a.grupo === g);
      const filtrados = q ? opts.filter((a) => norm(a.value).includes(q)) : opts;
      if (filtrados.length) gruposConItems.push({ grupo: g, opts: filtrados });
    }
    // Grupo "Otro" siempre visible si empata (o sin query)
    if (!q || norm(OTRO.value).includes(q)) {
      gruposConItems.push({ grupo: OTRO.grupo, opts: [OTRO] });
    }
    return gruposConItems;
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          id={id}
          data-testid={testid || `select-actividad_economica`}
          className={cn("h-9 w-full justify-between font-normal", !value && "text-muted-foreground")}
        >
          <span className="truncate">{value || "Seleccionar giro..."}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar giro..."
            value={query}
            onValueChange={setQuery}
            data-testid="actividad-economica-buscar"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>Sin resultados.</CommandEmpty>
            {items.map(({ grupo, opts }) => (
              <CommandGroup key={grupo} heading={grupo}>
                {opts.map((a) => (
                  <CommandItem
                    key={a.value}
                    value={a.value}
                    data-testid={`option-actividad-${a.value.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                    onSelect={() => {
                      onValueChange(a.value);
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === a.value ? "opacity-100" : "opacity-0")} />
                    <span>{a.value}{a.av ? " [AV]" : ""}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
